"use client";

import React, { ChangeEventHandler, useCallback, FC } from "react";

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import clsx from "clsx";

import { FILTER_DIVIDER } from "~/components/products-layout/constants";
import { VirtualList } from "~/components/virtual-list/component";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { COLOR_PRIMARY_BLACK } from "~/theme/colors";
import { CheckMark24 } from "~/theme/icons/components/check-mark";
import { Nullable } from "~/types/general.types";

import {
  AppliedFilters,
  CombinedColorFilter,
  HandleFiltersCallback,
} from "../../../../types";
import { FILTERS_CHECKBOX_SHARED_CLASSES } from "../filter-shared.constants";
import { ColorFilterContainer } from "../filter-shared.styled";

export interface ColorFilterProps {
  name?: string;
  label: string;
  onClick?: HandleFiltersCallback;
  filters: CombinedColorFilter[];
  appliedFilters?: Nullable<AppliedFilters>;
  className?: string;
}

const getEstimatedHeight = () => 48;

export const ColorFilter: FC<ColorFilterProps> = ({
  label,
  filters,
  onClick,
  appliedFilters,
  className,
  name,
}) => {
  const theme = useTheme();

  const colorImageWidth = theme.spacing(10);
  const getKey = useCallback(
    (data: CombinedColorFilter, index: number) => `${data.colorName}_${index}`,
    [],
  );
  const renderItem = useCallback(
    (data: CombinedColorFilter) => {
      const { count, colorImage, colorName, checkMarkColor } = data ?? {};
      const filterObjectKey = `${name ?? label}${FILTER_DIVIDER}${colorName}${FILTER_DIVIDER}${colorName}`;
      return (
        <ColorFilterContainer
          className={className}
          {...getTestAutomationProps("color-filter")}
        >
          <FormControlLabel
            classes={{
              root: FILTERS_CHECKBOX_SHARED_CLASSES.formControlRoot,
              label: FILTERS_CHECKBOX_SHARED_CLASSES.formControlLabel,
            }}
            control={
              <Checkbox
                name={filterObjectKey}
                value={count}
                id={filterObjectKey}
                onChange={onClick as ChangeEventHandler<HTMLInputElement>}
                checked={!!appliedFilters?.[filterObjectKey] || false}
                style={{
                  backgroundImage: `url(${colorImage?.src}?w=${colorImageWidth})`,
                }}
                checkedIcon={<CheckMark24 />}
                classes={{
                  root: FILTERS_CHECKBOX_SHARED_CLASSES.colorFilterCheckbox,
                  checked: clsx(
                    FILTERS_CHECKBOX_SHARED_CLASSES.colorFilterCheckboxChecked,
                    {
                      [FILTERS_CHECKBOX_SHARED_CLASSES.whiteCheckbox]:
                        checkMarkColor !== "dark",
                    },
                  ),
                }}
                {...getTestAutomationProps("checkbox")}
                sx={{ border: `1px solid ${COLOR_PRIMARY_BLACK}` }}
              />
            }
            label={
              <Box className={FILTERS_CHECKBOX_SHARED_CLASSES.filterItemLabel}>
                <Typography
                  variant="body2"
                  {...getTestAutomationProps("color-name")}
                >
                  {colorName}
                </Typography>

                <Typography variant="body2" {...getTestAutomationProps("count")}>
                  {count}
                </Typography>
              </Box>
            }
          />
        </ColorFilterContainer>
      );
    },
    [label, onClick, appliedFilters, className, name, colorImageWidth],
  );

  return (
    <VirtualList
      items={filters}
      renderItem={renderItem}
      getKey={getKey}
      estimateSize={getEstimatedHeight}
    />
  );
};
